import { useMemo, useRef } from 'react'
import RepeatRoundedIcon from '@mui/icons-material/RepeatRounded'
import {
  addDays,
  compareIsoDate,
  eventColorVar,
  HOURS,
  HOUR_ROW_HEIGHT,
  parseTimeMinutes,
  startOfWeek,
  toIsoDate,
  type CalendarEvent,
} from '../calendarHelpers'
import { Pressable } from '../../Common/Pressable'

interface WeekViewProps {
  focusedDate: Date
  allDayEvents: CalendarEvent[]
  timedEvents: CalendarEvent[]
  onEventClick: (event: CalendarEvent) => void
  onCellClick: (date: Date) => void
  onEventDragStart: (event: CalendarEvent, dateIso: string, minutes?: number) => void
  onEventDragOver: (dateIso: string, minutes?: number) => void
  onEventDragEnd: () => void
  draggingEventId?: string | null
}

const WEEKDAY_LABELS = ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT']

const todayIso = () => toIsoDate(new Date())

const minutesToPercent = (minutes: number) => (minutes / (24 * 60)) * 100

export const WeekView = ({
  focusedDate,
  allDayEvents,
  timedEvents,
  onEventClick,
  onCellClick,
  onEventDragStart,
  onEventDragOver,
  onEventDragEnd,
  draggingEventId,
}: WeekViewProps) => {
  const days = useMemo(() => {
    const start = startOfWeek(focusedDate)
    return Array.from({ length: 7 }, (_, idx) => addDays(start, idx))
  }, [focusedDate])
  const today = todayIso()
  const dayColRefs = useRef<Array<HTMLDivElement | null>>([])

  // All-day lane layout: greedy slot.
  const lanes = useMemo(() => {
    const out: CalendarEvent[][] = []
    for (const event of allDayEvents) {
      let placed = false
      for (const lane of out) {
        if (!lane.some((other) => compareIsoDate(other.endDate, event.startDate) >= 0 && compareIsoDate(other.startDate, event.endDate) <= 0)) {
          lane.push(event)
          placed = true
          break
        }
      }
      if (!placed) out.push([event])
    }
    return out
  }, [allDayEvents])

  const getMinutesFromY = (dayIdx: number, clientY: number): number | undefined => {
    const node = dayColRefs.current[dayIdx]
    if (!node) return undefined
    const bounds = node.getBoundingClientRect()
    const ratio = (clientY - bounds.top) / bounds.height
    return Math.max(0, Math.min(24 * 60 - 15, Math.round((ratio * 24 * 60) / 15) * 15))
  }

  return (
    <div className="calWeek" data-cal-view="week" onPointerUp={onEventDragEnd}>
      <div className="calWeekHead">
        <div className="calWeekHeadGutter" />
        {days.map((day) => {
          const iso = toIsoDate(day)
          return (
            <div key={iso} className={`calWeekHeadDay${iso === today ? ' isToday' : ''}`}>
              <span className="calWeekHeadWeekday">{WEEKDAY_LABELS[day.getDay()]}</span>
              <span className="calWeekHeadDate">{day.getDate()}</span>
            </div>
          )
        })}
      </div>

      <div className="calWeekAllDay">
        <div className="calWeekAllDayGutter">all-day</div>
        <div
          className="calWeekAllDayGrid"
          style={{ gridTemplateRows: `repeat(${Math.max(1, lanes.length)}, 24px)` }}
        >
          {days.map((_day, idx) => (
            <div key={`bg-${idx}`} className="calWeekAllDayCol" style={{ gridColumn: idx + 1, gridRow: `1 / -1` }} />
          ))}
          {lanes.map((lane, laneIdx) =>
            lane.map((event) => {
              const startIdx = days.findIndex((day) => toIsoDate(day) >= event.startDate)
              const endIdx = days.findIndex((day) => toIsoDate(day) > event.endDate)
              const colStart = startIdx === -1 ? 8 : Math.max(1, startIdx + 1)
              const colEnd = endIdx === -1 ? 8 : Math.max(colStart + 1, endIdx + 1)
              const isDragging = event.id === draggingEventId
              return (
                <div
                  key={event.id}
                  className={`calWeekAllDayBar calWeekAllDayBar--${event.kind}${isDragging ? ' isDragging' : ''}${event.isRecurring ? ' isRecurring' : ''}${event.isCancelled ? ' isCancelled' : ''}`}
                  style={{
                    gridColumn: `${colStart} / ${colEnd}`,
                    gridRow: laneIdx + 1,
                    ['--cal-accent' as string]: eventColorVar(event.kind),
                  } as React.CSSProperties}
                  onClick={() => onEventClick(event)}
                  onPointerDown={(pointerEvent) => {
                    pointerEvent.stopPropagation()
                    onEventDragStart(event, event.startDate)
                  }}
                  title={event.isRecurring ? `${event.title} · recurring` : event.title}
                >
                  {event.isRecurring && <span className="calWeekAllDayBarGlyph" aria-hidden><RepeatRoundedIcon fontSize="inherit" /></span>}
                  <span className="calWeekAllDayBarLabel">
                    {event.title}
                  </span>
                </div>
              )
            }),
          )}
        </div>
      </div>

      <div className="calWeekBody">
        <div className="calWeekHours">
          {HOURS.map((hour) => (
            <div key={hour} className="calWeekHourRow" style={{ height: HOUR_ROW_HEIGHT }}>
              <span>{`${hour.toString().padStart(2, '0')}:00`}</span>
            </div>
          ))}
        </div>
        <div className="calWeekColumns">
          {days.map((day, dayIdx) => {
            const iso = toIsoDate(day)
            const eventsForDay = timedEvents.filter((event) => event.startDate === iso)
            return (
              <div
                key={iso}
                className={`calWeekColumn${iso === today ? ' isToday' : ''}`}
                ref={(node) => {
                  dayColRefs.current[dayIdx] = node
                }}
                onPointerMove={(pointerEvent) => {
                  if (!draggingEventId) return
                  const minutes = getMinutesFromY(dayIdx, pointerEvent.clientY)
                  onEventDragOver(iso, minutes)
                }}
                onClick={(pointerEvent) => {
                  if ((pointerEvent.target as HTMLElement).closest('.calWeekTimedEvent')) return
                  onCellClick(day)
                }}
              >
                {HOURS.map((hour) => (
                  <div key={hour} className="calWeekHourSlot" style={{ height: HOUR_ROW_HEIGHT }} />
                ))}
                {eventsForDay.map((event) => {
                  const startMin = parseTimeMinutes(event.startTime) ?? 0
                  const endMin = parseTimeMinutes(event.endTime) ?? startMin + 60
                  const isDragging = event.id === draggingEventId
                  return (
                    <Pressable
                      key={event.id}
                      className={`calWeekTimedEvent calWeekTimedEvent--${event.kind}${isDragging ? ' isDragging' : ''}${event.isRecurring ? ' isRecurring' : ''}${event.isCancelled ? ' isCancelled' : ''}`}
                      style={{
                        top: `${minutesToPercent(startMin)}%`,
                        height: `${minutesToPercent(endMin - startMin)}%`,
                        ['--cal-accent' as string]: eventColorVar(event.kind),
                      } as React.CSSProperties}
                      onClick={(pointerEvent) => {
                        pointerEvent.stopPropagation()
                        onEventClick(event)
                      }}
                      onPointerDown={(pointerEvent) => {
                        pointerEvent.stopPropagation()
                        const minutes = getMinutesFromY(dayIdx, pointerEvent.clientY)
                        onEventDragStart(event, iso, minutes)
                      }}
                      title={event.isRecurring ? `${event.title} · recurring · ${event.startTime}–${event.endTime}` : `${event.title} ${event.startTime}–${event.endTime}`}
                    >
                      <span className="calWeekTimedEventTitle">
                        {event.isRecurring && <span className="calWeekTimedEventGlyph" aria-hidden><RepeatRoundedIcon fontSize="inherit" /> </span>}
                        {event.title}
                      </span>
                      <span className="calWeekTimedEventTime">{event.startTime} – {event.endTime}</span>
                    </Pressable>
                  )
                })}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
